// Classification-marking check (PLAN §5 step 1b). Runs on the header/footer/first page of an upload before
// any AI call. A marking is a label ("CONFIDENTIAL", "Internal use only", "سري"), not the word used in prose
// ("this is confidential, by the way"), so Latin single words only count in capitals or alone on a line.
// Uploaded text is data: it is only matched against these patterns, never interpreted.

interface MarkingPattern {
  label: string;
  re: RegExp;
}

const B = '(?<![\\p{L}\\p{N}])';
const E = '(?![\\p{L}\\p{N}])';
const word = (w: string, flags = 'u') => new RegExp(`${B}${w}${E}`, flags);

/** Ordered: longer labels first, so "STRICTLY CONFIDENTIAL" is reported instead of "CONFIDENTIAL". */
const PATTERNS: MarkingPattern[] = [
  // English
  { label: 'TOP SECRET', re: word('TOP[\\s-]+SECRET') },
  { label: 'STRICTLY CONFIDENTIAL', re: word('STRICTLY\\s+CONFIDENTIAL') },
  { label: 'CONFIDENTIAL', re: word('CONFIDENTIAL') },
  { label: 'SECRET', re: word('SECRET') },
  { label: 'RESTRICTED', re: word('RESTRICTED') },
  { label: 'Internal use only', re: word('(?:for\\s+)?internal\\s+use\\s+only', 'iu') },
  { label: 'For official use only', re: word('for\\s+official\\s+use\\s+only', 'iu') },
  { label: 'Do not forward', re: word('do\\s+not\\s+(?:forward|distribute|circulate|copy)', 'iu') },
  { label: 'Not for distribution', re: word('not\\s+for\\s+(?:distribution|circulation|publication)', 'iu') },
  { label: 'Source protection', re: word('SOURCE\\s+PROTECTION') },
  // French
  { label: 'SECRET DÉFENSE', re: word('SECRET[\\s-]+D[ÉE]FENSE') },
  { label: 'TRÈS SECRET', re: word('TR[ÈE]S\\s+SECRET') },
  { label: 'CONFIDENTIEL', re: word('CONFIDENTIEL(?:LE)?') },
  { label: 'Diffusion restreinte', re: word('diffusion\\s+restreinte', 'iu') },
  { label: 'Usage interne', re: word('(?:[àa]\\s+)?usage\\s+interne(?:\\s+uniquement)?', 'iu') },
  { label: 'Ne pas diffuser', re: word('ne\\s+pas\\s+(?:diffuser|transmettre)', 'iu') },
  // German, Spanish, Italian
  { label: 'VERTRAULICH', re: word('(?:STRENG\\s+)?VERTRAULICH') },
  { label: 'Nur für den Dienstgebrauch', re: word('nur\\s+f[üu]r\\s+den\\s+dienstgebrauch', 'iu') },
  { label: 'CONFIDENCIAL', re: word('CONFIDENCIAL') },
  { label: 'RISERVATO', re: word('RISERVATO') },
  // Arabic (no case: the header zone limits false positives)
  { label: 'سري للغاية', re: word('سر[ّ]?ي(?:ة)?\\s+للغاية') },
  { label: 'سري', re: word('سر[ّ]?ي(?:ة)?') },
  { label: 'محظور', re: word('محظور(?:ة)?') },
  { label: 'للاستخدام الداخلي فقط', re: word('للاستخدام\\s+الداخلي(?:\\s+فقط)?') },
  { label: 'محدود التداول', re: word('محدود(?:ة)?\\s+التداول') },
];

/** Title-case or lower-case single words count only when they stand alone on a line ("Confidential"). */
const STANDALONE: MarkingPattern[] = [
  { label: 'CONFIDENTIAL', re: /^[\s*_#|–—-]*(?:strictly\s+)?confidential[\s*_#|.:–—-]*$/imu },
  { label: 'CONFIDENTIEL', re: /^[\s*_#|–—-]*confidentiel(?:le)?[\s*_#|.:–—-]*$/imu },
  { label: 'RESTRICTED', re: /^[\s*_#|–—-]*restricted[\s*_#|.:–—-]*$/imu },
  { label: 'SECRET', re: /^[\s*_#|–—-]*secret[\s*_#|.:–—-]*$/imu },
];

/** A template's header marking declared boilerplate (PLAN §2), so it doesn't prompt for every sheet. */
export interface BoilerplateRule {
  /** Template key, e.g. `actor-profile-sheet`. */
  template: string;
  /** The document is this template when its text matches. */
  detect: RegExp;
  /** Marking text printed on every copy of the template. */
  markings: string[];
}

/**
 * Built-in until the schema registry (task 7) lets admins declare boilerplate per template: every Actor Profile
 * Sheet carries "CONFIDENTIAL — Internal use only" in its header (all 60 real sheets and the fixtures).
 */
export const DEFAULT_BOILERPLATE: BoilerplateRule[] = [
  { template: 'actor-profile-sheet', detect: /ACTOR\s+PROFILE\s+SHEET/i, markings: ['CONFIDENTIAL — Internal use only'] },
];

/** How much of the body counts as "header / first page" and "footer". */
export const MARKING_HEAD_CHARS = 2500;
export const MARKING_TAIL_CHARS = 1000;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Matches the boilerplate text however dashes and spacing were typed. */
const boilerplateRe = (text: string) =>
  new RegExp(
    text
      .trim()
      .split(/\s*[—–-]\s*|\s+/)
      .map(escapeRe)
      .join('[\\s—–|:-]+'),
    'giu',
  );

export interface MarkingInput {
  /** Body text in reading order (first page first). */
  body: string;
  /** Header and footer parts (DOCX headers, ODT styles…), scanned in full. */
  headersFooters?: string[];
}

export interface MarkingResult {
  /** Distinct markings found outside boilerplate, in the order of PATTERNS. */
  markings: string[];
  /** Template whose boilerplate marking was ignored, if any. */
  boilerplateTemplate?: string;
}

export function detectMarkings(input: MarkingInput, boilerplate: BoilerplateRule[] = DEFAULT_BOILERPLATE): MarkingResult {
  const body = input.body.normalize('NFC');
  const zones = [
    body.slice(0, MARKING_HEAD_CHARS),
    body.length > MARKING_HEAD_CHARS ? body.slice(-MARKING_TAIL_CHARS) : '',
    ...(input.headersFooters ?? []).map(h => h.normalize('NFC')),
  ];
  const everything = `${body.slice(0, 20_000)}\n${zones.slice(2).join('\n')}`;
  const template = boilerplate.find(b => b.detect.test(everything));
  let text = zones.join('\n');
  if (template) for (const m of template.markings) text = text.replace(boilerplateRe(m), ' ');

  const found: string[] = [];
  const covered: Array<[number, number]> = [];
  for (const p of PATTERNS) {
    const re = new RegExp(p.re.source, p.re.flags.includes('g') ? p.re.flags : `${p.re.flags}g`);
    for (const m of text.matchAll(re)) {
      const start = m.index ?? 0;
      const end = start + m[0].length;
      // "STRICTLY CONFIDENTIAL" also contains "CONFIDENTIAL": report the longer label only.
      if (covered.some(([s, e]) => start >= s && end <= e)) continue;
      covered.push([start, end]);
      if (!found.includes(p.label)) found.push(p.label);
    }
  }
  for (const p of STANDALONE) if (p.re.test(text) && !found.some(f => f.toUpperCase().includes(p.label))) found.push(p.label);
  return { markings: found, ...(template ? { boilerplateTemplate: template.template } : {}) };
}

/** The question shown to the uploader (PLAN §5 step 1b). */
export const markingQuestion = (markings: string[]) => `This document is marked ${markings[0] ?? 'as sensitive'}. Switch to confidential?`;
